/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	Pressable,
	View,
	Text,
	ErrorText,
	TextField,
	Image,
	Button,
	Icon,
} from '@components/atoms'
import { Footer } from '@components/systems'
import loginBg from '@public/images/pages/customerLogin/login-bg.jpg'

import { useAppSelector, useAppDispatch, customerAuthActions } from '@redux/index'
import { produce } from 'immer'
import { useNavigate } from 'react-router-dom'
import { grey } from '@mui/material/colors'

const CustomerLogin = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// redux state
	const loginErrorMessage: string = useAppSelector(
		state => state.customerAuth.loginErrorMessage,
	)

	// state
	const [account, setAccount] = React.useState({ email: '', password: '' })

	const onIdChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const str = e.target.value
			const newAccount = produce(account, draft => {
				draft.email = str
			})

			setAccount(newAccount)
		},
		[account],
	)

	const onPwChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const str = e.target.value
			const newAccount = produce(account, draft => {
				draft.password = str
			})

			setAccount(newAccount)
		},
		[account],
	)

	const onInputEnterSubmit = React.useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				onLoginClick()
			}
		},
		[account],
	)

	const onLoginClick = React.useCallback(() => {
		if (account.email !== '' && account.password !== '') {
			reduxDispatch(
				customerAuthActions.LOGIN({ email: account.email, password: account.password }),
			)
		} else {
			reduxDispatch(
				customerAuthActions.LOGIN_ERROR_MESSAGE(
					'Please check your login information again.',
				),
			)
		}
	}, [account])

	return (
		<View css={style}>
			<View className="customerLoginOuterView">
				<View className="loginPaper">
					<View className="loginImageView">
						<Image src={loginBg} className="loginImage" />
					</View>

					<View className="loginInputView">
						<Pressable className="backButton" css={{ cursor: 'default' }}>
							<Icon name="arrow-left" className="backButtonIcon" />
						</Pressable>
						<Text category="h4" className="inputTitle">
							Login
						</Text>
						<View className="inputView">
							<TextField
								type="text"
								label="Email"
								size="small"
								defaultValue={account.email}
								onChange={onIdChange}
								onKeyUp={onInputEnterSubmit}
							/>
						</View>
						<View className="inputView">
							<TextField
								type="password"
								label="Password"
								size="small"
								onChange={onPwChange}
								onKeyUp={onInputEnterSubmit}
							/>
						</View>

						<View className="loginButtonDiv">
							{loginErrorMessage !== '' ? (
								<ErrorText className="errorMsg">{loginErrorMessage}</ErrorText>
							) : null}

							<Button onClick={onLoginClick} className="loginButton" variant="contained">
								Login
							</Button>

							<Text hint="500" css={{ textAlign: 'center' }}>
								---- or ----
							</Text>

							<Button
								onClick={() => navigate('/customer/join')}
								className="loginButton"
								variant="contained"
							>
								Join the Membership
							</Button>

							<Pressable
								onClick={() => navigate('/customer/forgot-password')}
								css={{ width: '100%', textAlign: 'center' }}
							>
								<Text category="p2" hint="500" underline={true}>
									Forgot Password?
								</Text>
							</Pressable>
						</View>
					</View>
				</View>

				<View className="loginBackgroundView">
					<View className="loginBgView"></View>
					<Image src={loginBg} className="loginBackground" />
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default CustomerLogin

const style = css({
	position: 'relative',
	width: '100%',

	'.customerLoginOuterView': {
		position: 'relative',
		height: 'calc(100vh - 10rem)',
		width: '100%',
		overflow: 'hidden',

		'.loginPaper': {
			position: 'absolute',
			zIndex: 5,
			display: 'flex',
			width: '60rem',
			height: '35rem',
			top: 0,
			bottom: 0,
			right: 0,
			left: 0,
			margin: 'auto',
			background: '#FFF',
			borderRadius: '10px',
			overflow: 'hidden',

			'.loginImageView': {
				width: 'calc(100% - 25rem)',
				overflow: 'hidden',

				'.loginImage': {
					width: 'auto',
					height: '100%',
					marginLeft: '-7rem',
				},
			},

			'.loginInputView': {
				width: '25rem',
				padding: '2rem',
				paddingTop: '2rem',
				textAlign: 'left',

				'.backButton': {
					'.backButtonIcon': {
						color: 'white',
					},
				},

				'.inputTitle': {
					textAlign: 'center',
				},

				'.inputView': {
					width: '100%',
					marginBottom: '1.2rem',
				},

				'.input': {
					width: '100%',
				},

				'.loginButtonDiv': {
					position: 'relative',
					width: '100%',
					marginBottom: '0.8rem',
					paddingTop: 0,

					'.idSaveView': {
						display: 'flex',
						alignItems: 'center',
					},

					'.loginButton': {
						width: '100%',
						height: '2.6rem',
						background: grey[900],
					},
				},
			},
		},

		'.loginBackgroundView': {
			position: 'absolute',
			zIndex: 0,
			top: 0,
			bottom: 0,
			width: '100%',
			height: 'auto',
			overflow: 'hidden',

			'.loginBgView': {
				position: 'absolute',
				zIndex: 1,
				top: 0,
				bottom: 0,
				width: '100%',
				height: '100%',
				background: grey[900],
				opacity: 0.5,
			},

			'.loginBackground': {
				height: 'auto',
				width: '110%',
				marginTop: '-1rem',
				marginLeft: '-1rem',
				filter: 'blur(10px)',
			},
		},
	},

	'@media (max-width: 1050px)': {
		'.customerLoginOuterView': {
			'.loginPaper': {
				width: 'calc(100% - 4rem)',
			},
		},
	},

	'@media (max-width: 950px)': {
		'.customerLoginOuterView': {
			'.loginPaper': {
				'.loginImageView': {
					width: 'calc(100% - 40vw)',

					'.loginImage': {
						height: '120%',
						marginLeft: '-20vw',
					},
				},

				'.loginInputView': {
					width: '40vw',
				},
			},
			'.loginBackgroundView': {
				'.loginBackground': {
					width: '150%',
				},
			},
		},
	},

	'@media (max-width: 760px)': {
		'.customerLoginOuterView': {
			'.loginPaper': {
				maxWidth: '28rem',
				width: 'calc(100vw - 4rem)',

				'.loginImageView': {
					display: 'none',
				},

				'.loginInputView': {
					width: '100%',
				},
			},
		},
	},

	'@media (max-width: 400px)': {
		'.customerLoginOuterView': {
			'.loginPaper': {
				width: 'calc(100vw - 1rem)',
			},
		},
	},
})
